import { Colour } from "@interfaces/IGeofences";
import { IRunBlockRootScope } from "@interfaces/IRunBlockRootScope";

export interface IColour {
    solidColour: string;
    transparentColour?: string;

    isJourneyColour?: boolean;
    isTagColour?: boolean;
    colourEnum?: Colour;
    description: string;
}
// Nice autumnal chart colours
//["#ECD078","#D95B43","#C02942","#542437","#53777A"];

export class ColourService {

    // Based on Flat UI Colours
    colours: IColour[] = [];
    journeyColours: IColour[] = [];
    tagColours: IColour[] = [];

    // From scss
    greenColor = "#209058";
    orangeColor = "#ffa500"; // orange
    redColor = "#cf4946";
    greyColor = "rgba(0,0,0,0.1)";

    static $inject = ["$rootScope"];

    constructor(private readonly $rootScope: IRunBlockRootScope) {
        this.createColours();
    }

    // viget colours
    // https://www.viget.com/articles/add-colors-to-your-palette-with-color-mixing/
    vigetColours = ["#0d3c55", "#0f5b78", "#117899", "#1395ba", "#5ca793", "#a2b86c", "#ebc844", "#ecaa38", "#ef8b2c", "#f16c20", "#d94e1f", "#c02e1d"];

    getVigetColor(index: number, maxDimensions: number = this.vigetColours.length, offset = 0, vigetColours = this.vigetColours): string {

        let multiplier = Math.floor(vigetColours.length / maxDimensions);
        multiplier = Math.max(multiplier, 1);

        index = index * multiplier;
        index += offset;
        index = index % vigetColours.length;

        return vigetColours[index];
    }

    getColour(index: number, journeyColoursOnly: boolean): IColour {
        const idx = this.getColourIndex(index, journeyColoursOnly);

        if (journeyColoursOnly) {
            return this.journeyColours[idx];
        }

        return this.colours[idx];
    }

    getColourIndex(index: number, journeyColoursOnly: boolean): number {
        if (journeyColoursOnly) {
            return index % this.journeyColours.length;
        }

        return index = index % this.colours.length;
    }

    createTransparentColor(color: string, opacity: number): string {
        if (color.indexOf("rgba") >= 0) {
            return color;
        }
        else if (color.indexOf("rgb") >= 0) {
            color = color.replace(")", "");
            color += `,${opacity})`;
            return color;
        }
        else {
            color = color.replace('#', '');
            const rgb = this.hexToRgb(color);
            return `rgba(${rgb},${opacity})`;
        }
    }

    getJourneyBorderClassName(i: number) {
        return `jc-${i}-border-color`;
    }

    getJourneyBackgroundClassName(i: number) {
        return `jc-${i}-bg-color`;
    }
    
    // ------------------------------------------

    private createColours() {

        this.colours.push({ solidColour: "#3498db", description: "Peter River", isJourneyColour: true });
        this.colours.push({ solidColour: "#2980b9", description: "Belize Hole", isTagColour: true, colourEnum: Colour.Blue });

        this.colours.push({ solidColour: "#9b59b6", description: "Amethyst" });
        this.colours.push({ solidColour: "#8e44ad", description: "Wisteria", isJourneyColour: true, isTagColour: true, colourEnum: Colour.Purple });

        this.colours.push({ solidColour: "#f1c40f", description: "Sun Flower", isTagColour: true, colourEnum: Colour.Yellow });
        this.colours.push({ solidColour: "#f39c12", description: "Orange", isTagColour: true, colourEnum: Colour.Orange });

        this.colours.push({ solidColour: "#e67e22", description: "Carrot", isJourneyColour: true });
        this.colours.push({ solidColour: "#d35400", description: "Pumpkin" });

        this.colours.push({ solidColour: "#1abc9c", description: "Turquoise" });
        this.colours.push({ solidColour: "#16a085", description: "Grean Sea", isJourneyColour: false, isTagColour: true, colourEnum: Colour.Green });

        this.colours.push({ solidColour: "#2ecc71", description: "Emerald" });
        this.colours.push({ solidColour: "#27ae60", description: "Nephritis", isJourneyColour: true });

        this.colours.push({ solidColour: "#e74c3c", description: "Alizarin", isJourneyColour: true });
        this.colours.push({ solidColour: "#c0392b", description: "Pomegranate", isTagColour: true, colourEnum: Colour.Red });

        this.colours.push({ solidColour: "#34495e", description: "Wet Asphalt", isTagColour: true, colourEnum: Colour.Black });
        this.colours.push({ solidColour: "#2c3e50", description: "Midnight Blue", isJourneyColour: true });

        //this.colours.push({ solidColour: "#ecf0f1", description: "Clouds" });
        this.colours.push({ solidColour: "#bdc3c7", description: "Silver" });

        this.colours.push({ solidColour: "#95a5a6", description: "Concrete", isTagColour: true, colourEnum: Colour.Grey });
        this.colours.push({ solidColour: "#7f8c8d", description: "Asbestos" });

        // Create transparent versions and add to journey colours if required
        this.colours.forEach(colour => {
            colour.transparentColour = this.createTransparentColor(colour.solidColour, 0.4);

            if (colour.isJourneyColour) {
                this.journeyColours.push(colour);
            }
            if (colour.isTagColour) {
                this.tagColours.push(colour);
            }
        });

        this.createStylesForJourneyColors();
    }

    private hexToRgb(hex) {
        const bigint = parseInt(hex, 16);
        const r = (bigint >> 16) & 255;
        const g = (bigint >> 8) & 255;
        const b = bigint & 255;

        return r + "," + g + "," + b;
    }

    private getRandomColor() {
        const letters = '0123456789ABCDEF';
        let color = '#';
        for (let i = 0; i < 6; i++) {
            color += letters[Math.floor(Math.random() * 16)];
        }

        return color;
    }

    private createStylesForJourneyColors() {
        // Create styles
        this.$rootScope.runtimeStyles = this.$rootScope.runtimeStyles || "";

        for (let i = 0; i < this.journeyColours.length; i++) {
            const color = this.journeyColours[i].solidColour;

            this.$rootScope.runtimeStyles += `.${this.getJourneyBorderClassName(i)}{border-color:${color}!important;}`;
            this.$rootScope.runtimeStyles += `.${this.getJourneyBackgroundClassName(i)}{background-color:${color}!important;}`;
        }
    }
}
